import React from "react";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";

import Login from "src/container/Login";
import { ForgotPassword } from "src/container/Login/ForgotPassword";
import Dashboard from "src/container/Dashboard";
import Customers from "../container/Customers/Customers";
import CustomerDetail from "src/container/Customers/CustomerDetail/CustomerDetail";
import CustomerDeviceDetail from "src/container/Customers/CustomerDetail/DevicesTab/CustomerDeviceDetail";
import TachographBlankPage from "src/container/Tachograph/TachographBlankPage";
import TachographCardsPage from "src/container/Customers/TachographCards/TachographCardsPage";
import AuditLogDataPage from "src/container/Customers/AuditLogData/AuditLogDataPage";
import DriverActivitiesPage from "src/container/Customers/DriverActivities/DriverActivitiesPage";
import RawDataLog from "src/container/Customers/RawDataLog";
import RoutePresetDetailPage from "src/container/Customers/CustomerDetail/DevicesTab/RoutePresetDetailPage";
import Warehouse, { WarehouseHierarchy } from "src/container/Warehouse";
import Users from "src/container/Users/Users";
import AllUnits from "src/container/WarehouseManagement/AllUnits/AllUnits";
import UnitsManagement from "src/container/DeviceManagement/UnitsManagement";
import VehicleBrands from "src/container/Administration/VehicleBrands";
import VehicleModels from "src/container/Administration/VehicleModels";
import VehicleTypes from "src/container/Administration/VehicleTypes";
import AddModelProperties from "src/container/Administration/AddModelProperties";
import TemperatureSensorTypes from "src/container/Administration/TemperatureSensorTypes";
import SwitchOnOffStates from "src/container/Administration/SwitchOnOffStates";
import RelayStatusTypes from "../container/Administration/RelayStatusTypes";
import {
  ParameterGroupsSetupPage,
  ParametersSetupPage,
  CustomerTypeSetupPage,
  CustomerSourceSetupPage,
  CorporationPositionSetupPage,
  BusinessActivitiesSetupPage,
  UnitBrandSetupPage,
  UnitModelsSetupPage,
} from "src/container/Administration/Setups";
import TireMakes from "src/container/Administration/TireMakes";
import TireModels from "../container/Administration/TireModels";
import TireMeasures from "../container/Administration/TireMeasures";
import TirePresets from "../container/Administration/TirePresets";
import TirePresetConfig from "../container/Administration/TirePresets/TirePresetConfig";
import {
  MappingPresetDetailPage,
  MappingPresetListPage,
} from "src/container/Customers/CustomerDetail/DevicesTab/Mapping/MappingPresetPages";
import {
  CustomersSettingsPage,
  TireSettingsPage,
  UnitsSettingsPage,
  VehicleSettingsPage,
} from "src/container/Administration/Settings";
import PrivacyPolicyForm from "src/container/PrivacyPolicy/PrivacyPolicyForm";
import SettingsPage from "src/container/Settings/SettingsPage";

import VehicleStateCounterPage from "src/container/VehicleStateCounter/VehicleStateCounter";

export interface AppRoute {
  path: string;
  element: ReactElement;
}

const RoutePlaceholder: React.FC<{ title: string }> = ({ title }) => (
  <div className="p-6">
    <h1 className="mb-2 text-2xl font-bold">{title}</h1>
    <p className="text-gray-600">
      TODO: This page has not been ported to the new TypeScript project yet.
    </p>
  </div>
);

export const publicRoutes: AppRoute[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
];

export const privateRoutes: AppRoute[] = [
  {
    path: "/vehicle-state-counter",
    element: <VehicleStateCounterPage />,
  },
  {
    path: "/",
    element: <Dashboard />,
  },
  {
    path: "/administration/privacy-policy",
    element: <PrivacyPolicyForm />,
  },
  {
    path: "/customers",
    element: <Customers />,
  },
  {
    path: "/device-management/install",
    element: <UnitsManagement />,
  },
  {
    path: "/device-management/uninstall",
    element: <UnitsManagement />,
  },
  {
    path: "/device-management/change",
    element: <UnitsManagement />,
  },
  {
    path: "/customers/add",
    element: <RoutePlaceholder title="Add Customer" />,
  },
  {
    path: "/device-management",
    element: <UnitsManagement />,
  },
  {
    path: "/user-management",
    element: <RoutePlaceholder title="User Management" />,
  },
  {
    path: "/settings",
    element: <SettingsPage />,
  },
  {
    path: "/setting",
    element: <Navigate to="/settings" replace />,
  },
  {
    path: "/reports",
    element: <RoutePlaceholder title="Reports" />,
  },
  {
    path: "/warehouse-management/import-unit",
    element: <RoutePlaceholder title="Import Unit" />,
  },
  {
    path: "/device-management/connect-to-sim",
    element: <RoutePlaceholder title="Connect Device to SIM" />,
  },
  {
    path: "/clients/client-preset",
    element: <RoutePlaceholder title="Client Presets" />,
  },
  {
    path: "/clients/client-preset/add",
    element: <RoutePlaceholder title="Add Client Preset" />,
  },
  {
    path: "/administration/general-setups/unit-convention/currency",
    element: <RoutePlaceholder title="Currency Setup" />,
  },
  {
    path: "/administration/general-setups/insurance/type",
    element: <RoutePlaceholder title="Insurance Type" />,
  },
  {
    path: "/administration/general-setups/vehicle/type",
    element: <VehicleTypes />,
  },
  {
    path: "/administration/general-setups/unit-convention/volumn",
    element: <RoutePlaceholder title="Volume Unit" />,
  },
  {
    path: "/administration/general-setups/unit-convention/distance",
    element: <RoutePlaceholder title="Distance Unit" />,
  },
  {
    path: "/administration/general-setups/unit-convention/temperature",
    element: <RoutePlaceholder title="Temperature Unit" />,
  },
  {
    path: "/administration/general-setups/unit-convention/weight",
    element: <RoutePlaceholder title="Weight Unit" />,
  },
  {
    path: "/administration/general-setups/unit-convention/meas-fuel-consumption",
    element: <RoutePlaceholder title="Fuel Consumption Unit" />,
  },
  {
    path: "/administration/general-setups/payment/payment-period",
    element: <RoutePlaceholder title="Payment Period" />,
  },
  {
    path: "/administration/general-setups/payment/pay-sub-until",
    element: <RoutePlaceholder title="Pay Subscription Until" />,
  },
  {
    path: "/administration/general-setups/payment/ppr-payment-term",
    element: <RoutePlaceholder title="PPR Payment Term" />,
  },
  {
    path: "/administration/general-setups/client/type",
    element: <CustomerTypeSetupPage />,
  },
  {
    path: "/administration/general-setups/client/source",
    element: <CustomerSourceSetupPage />,
  },
  {
    path: "/administration/general-setups/unit-convention/languages",
    element: <RoutePlaceholder title="Languages" />,
  },
  {
    path: "/administration/general-setups/client/type-of-client-business-activities",
    element: <BusinessActivitiesSetupPage />,
  },
  {
    path: "/administration/general-setups/log/log-types",
    element: <RoutePlaceholder title="Log Types" />,
  },
  {
    path: "/administration/general-setups/device/status",
    element: <RoutePlaceholder title="Device Status" />,
  },
  {
    path: "/administration/general-setups/log/logActivityTypes",
    element: <RoutePlaceholder title="Log Activity Types" />,
  },
  {
    path: "/administration/general-setups/user/type",
    element: <RoutePlaceholder title="User Type" />,
  },
  {
    path: "/administration/general-setups/user/roles",
    element: <RoutePlaceholder title="User Roles" />,
  },
  {
    path: "/administration/general-setups/warehouse/type",
    element: <RoutePlaceholder title="Warehouse Type" />,
  },
  {
    path: "/administration/general-setups/units/unit-status",
    element: <RoutePlaceholder title="Unit Status" />,
  },
  {
    path: "/administration/general-setups/units/unit-operation",
    element: <RoutePlaceholder title="Unit Operation" />,
  },
  {
    path: "/administration/general-setups/units/unit-type",
    element: <RoutePlaceholder title="Unit Type" />,
  },
  {
    path: "/administration/general-setups/unit-convention/time-zones",
    element: <RoutePlaceholder title="Time Zones" />,
  },
  {
    path: "/administration/general-setups/client/position",
    element: <CorporationPositionSetupPage />,
  },
  {
    path: "/AllDevices",
    element: <RoutePlaceholder title="All Devices" />,
  },
  {
    path: "/warehouse/overview",
    element: <RoutePlaceholder title="Warehouse Overview" />,
  },
  {
    path: "/warehouse/inventory",
    element: <RoutePlaceholder title="Warehouse Inventory" />,
  },
  {
    path: "/warehouse/inventory-summary",
    element: <RoutePlaceholder title="Inventory Summary" />,
  },
  {
    path: "/warehouse-management/all-units",
    element: <AllUnits />,
  },
  {
    path: "/warehouse-management/warehouse",
    element: <Warehouse />,
  },
  {
    path: "/warehouse-management/area/:warehouseName/:warehouseId",
    element: <WarehouseHierarchy page="area" />,
  },
  {
    path: "/warehouse-management/lane/:warehouseName/:warehouseId/:areaName/:areaId",
    element: <WarehouseHierarchy page="lane" />,
  },
  {
    path: "/warehouse-management/add-unit",
    element: <RoutePlaceholder title="Add Unit" />,
  },
  {
    path: "/warehouse-management/self/:warehouseName/:warehouseId/:areaName/:areaId/:laneName/:laneId",
    element: <WarehouseHierarchy page="shelf" />,
  },
  {
    path: "/warehouse-management/position/:warehouseName/:warehouseId/:areaName/:areaId/:laneName/:laneId/:shelfName/:shelfId",
    element: <WarehouseHierarchy page="position" />,
  },
  {
    path: "/warehouse-management/position-rules",
    element: <RoutePlaceholder title="Position Rules" />,
  },
  {
    path: "/administration/settings/vehicle-settings",
    element: <Navigate to="/administration/settings/vehicle-settings/brand" replace />,
  },
  {
    path: "/administration/settings/vehicle-settings/brand",
    element: <VehicleSettingsPage tab="brand" />,
  },
  {
    path: "/administration/settings/vehicle-settings/model",
    element: <VehicleSettingsPage tab="model" />,
  },
  {
    path: "/administration/settings/vehicle-settings/type",
    element: <VehicleSettingsPage tab="type" />,
  },
  {
    path: "/administration/settings/tire-settings",
    element: <Navigate to="/administration/settings/tire-settings/brand" replace />,
  },
  {
    path: "/administration/settings/tire-settings/brand",
    element: <TireSettingsPage tab="brand" />,
  },
  {
    path: "/administration/settings/tire-settings/model",
    element: <TireSettingsPage tab="model" />,
  },
  {
    path: "/administration/settings/tire-settings/measure",
    element: <TireSettingsPage tab="measure" />,
  },
  {
    path: "/administration/settings/tire-settings/preset",
    element: <TireSettingsPage tab="preset" />,
  },
  {
    path: "/administration/settings/units-settings",
    element: <Navigate to="/administration/settings/units-settings/brand" replace />,
  },
  {
    path: "/administration/settings/units-settings/brand",
    element: <UnitsSettingsPage tab="brand" />,
  },
  {
    path: "/administration/settings/units-settings/model",
    element: <UnitsSettingsPage tab="model" />,
  },
  {
    path: "/administration/settings/customers-settings",
    element: <Navigate to="/administration/settings/customers-settings/type" replace />,
  },
  {
    path: "/administration/settings/customers-settings/type",
    element: <CustomersSettingsPage tab="type" />,
  },
  {
    path: "/administration/settings/customers-settings/source",
    element: <CustomersSettingsPage tab="source" />,
  },
  {
    path: "/administration/settings/customers-settings/position",
    element: <CustomersSettingsPage tab="position" />,
  },
  {
    path: "/administration/setups/vehicle-make",
    element: <VehicleBrands />,
  },
  {
    path: "/administration/setups/vehicle-model",
    element: <VehicleModels />,
  },
  {
    path: "/administration/setups/temperature-sensor-types",
    element: <TemperatureSensorTypes />,
  },
  {
    path: "/administration/setups/switch-on-off-state",
    element: <SwitchOnOffStates />,
  },
  {
    path: "/administration/setups/relay-status-type",
    element: <RelayStatusTypes />,
  },
  {
    path: "/administration/setups/tire-make",
    element: <TireMakes />,
  },
  {
    path: "/administration/setups/tire-model",
    element: <TireModels />,
  },
  {
    path: "/administration/setups/tire-measure",
    element: <TireMeasures />,
  },
  {
    path: "/administration/setups/tire-preset",
    element: <TirePresets />,
  },
  {
    path: "/administration/setups/tire-preset-config",
    element: <TirePresetConfig />,
  },
  {
    path: "/administration/setups/tire-preset-config-edit",
    element: <TirePresetConfig />,
  },
  {
    path: "/setups/insurance-company",
    element: <RoutePlaceholder title="Insurance Company" />,
  },
  {
    path: "/administration/setups/unit-brand",
    element: <UnitBrandSetupPage />,
  },
  {
    path: "/administration/setups/unit-models",
    element: <UnitModelsSetupPage />,
  },
  {
    path: "/administration/setups/parameter-groups",
    element: <ParameterGroupsSetupPage />,
  },
  {
    path: "/administration/setups/parameters",
    element: <ParametersSetupPage />,
  },
  {
    path: "/administration/setups/add-model-properties",
    element: <AddModelProperties />,
  },
  {
    path: "/administration/setups/model-propieties",
    element: <AddModelProperties />,
  },
  {
    path: "/customers/:clientId",
    element: <CustomerDetail />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId",
    element: <CustomerDeviceDetail />,
  },
  {
    path: "/customers/raw-data-log",
    element: <RawDataLog />,
  },
  {
    path: "/customers/audit-log-data",
    element: <AuditLogDataPage />,
  },
  {
    path: "/tachograph/download-log",
    element: <TachographBlankPage titleKey="tachograph_download_log" />,
  },
  {
    path: "/tachograph/models",
    element: <TachographBlankPage titleKey="tachograph_models" />,
  },
  {
    path: "/customers/ddd-files",
    element: <DriverActivitiesPage />,
  },
  {
    path: "/customers/tachograph-cards",
    element: <TachographCardsPage />,
  },
  {
    path: "/user-management/users",
    element: <Users />,
  },
  {
    path: "/user-management/add-or-edit-user",
    element: <RoutePlaceholder title="Add or Edit User" />,
  },
  {
    path: "/user-management/audit-log",
    element: <RoutePlaceholder title="User Audit Log" />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId/presets",
    element: <MappingPresetListPage />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId/route/presets",
    element: <RoutePlaceholder title="Route Presets" />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId/refueling/presets",
    element: <RoutePlaceholder title="Refueling Presets" />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId/presets/:presetId",
    element: <MappingPresetDetailPage />,
  },
  {
    path: "/customers/:clientId/:unitId/:vehicleId/route/presets/:presetId",
    element: <RoutePresetDetailPage />,
  },
];
